/**
 * Pure helper for building the public B2B-portal URL of a CMS page.
 *
 * Used by `PageDetails`' "Public URL" line. Ancestor slugs are read from a
 * flat `GET_PAGES_TREE` result (see `pageQueries.ts`) rather than a
 * backend-computed `ancestors` field: the backend's `ancestors` resolver
 * stops at the first unpublished ancestor and silently returns a TRUNCATED
 * chain, which would produce a shorter, wrong URL with no way to detect it.
 * Walking the tree ourselves lets us both build the full chain and report
 * *why* an ancestor breaks resolution.
 */

/**
 * Languages the B2B portal actually serves
 * (`vilmers-b2b-portal/src/lib/i18n/config.ts`, `src/middleware.ts`). Any
 * other enabled language (e.g. the admin's `lt`) gets redirected by the
 * portal middleware (`/lt/x` -> `/en/lt/x`) and 404s.
 */
export const B2B_PORTAL_LANGUAGES = ['en', 'fr', 'de'] as const

export interface PageTreeNodeForUrl {
  id: string
  code: string
  published: boolean
  parentId: string | null
  page_profiles: { language: string; slug?: string | null; title?: string | null }[]
}

export interface PagePublicUrlResult {
  /**
   * Prospective URL, computed best-effort. `null` only for pages with no
   * standalone URL at all (currently just `login`, whose content is embedded
   * in the account layout) — every other case always returns a string, even
   * when `clickable` is false.
   */
  url: string | null
  /** Whether `url` actually resolves on the portal right now. */
  clickable: boolean
  /** Short human-readable reason shown next to a disabled/absent URL. */
  reason: string | null
}

function profileFor(node: PageTreeNodeForUrl, language: string) {
  return node.page_profiles.find(p => p.language === language)
}

function displayName(node: PageTreeNodeForUrl, language: string): string {
  return (
    profileFor(node, language)?.title || node.page_profiles[0]?.title || node.code
  )
}

/**
 * Root-first ancestor chain for `pageId` (excludes the page itself).
 * Defensive only: bails out (without throwing) on a parent-chain cycle or a
 * `parentId` missing from `pages` (stale cache / deleted ancestor) — neither
 * should happen with real data, but this must never crash the page.
 */
function getAncestors(
  pages: PageTreeNodeForUrl[],
  pageId: string
): PageTreeNodeForUrl[] {
  const byId = new Map(pages.map(p => [p.id, p]))
  const ancestors: PageTreeNodeForUrl[] = []
  const visited = new Set<string>([pageId])
  let parentId = byId.get(pageId)?.parentId ?? null

  while (parentId) {
    if (visited.has(parentId)) break
    visited.add(parentId)
    const node = byId.get(parentId)
    if (!node) break
    ancestors.unshift(node)
    parentId = node.parentId ?? null
  }

  return ancestors
}

export function buildPagePublicUrl(params: {
  baseUrl: string
  pageId: string
  code: string
  published: boolean
  /** Currently selected admin-UI language, e.g. `en`, `fr`, `de`, `lt`, ... */
  language: string
  /** Current (possibly-unsaved) slug value for `language`. */
  slug: string | undefined
  pages: PageTreeNodeForUrl[]
}): PagePublicUrlResult {
  const { baseUrl, pageId, code, published, language, slug, pages } = params

  const languageSupported = (B2B_PORTAL_LANGUAGES as readonly string[]).includes(
    language
  )
  const languageReason = `Portal has no ${language.toUpperCase()} site`

  // `login`'s content is embedded in the account layout — no standalone route.
  if (code === 'login') {
    return {
      url: null,
      clickable: false,
      reason: 'Embedded in the account page — no standalone URL',
    }
  }

  // Root page — hardcoded to `/{lang}`, not slug-driven. Renders regardless
  // of `published` (FIND_PAGE_BY_CODE has no published filter for it), so no
  // "unpublished" warning applies here.
  if (code === 'home-page') {
    const url = `${baseUrl}/${language}`
    return languageSupported
      ? { url, clickable: true, reason: null }
      : { url, clickable: false, reason: languageReason }
  }

  // Hardcoded route dir, not slug-driven.
  if (code === 'become-partner') {
    const url = `${baseUrl}/${language}/become-partner`
    if (!published) {
      return {
        url,
        clickable: false,
        reason: 'Unpublished — will 404 until published',
      }
    }
    return languageSupported
      ? { url, clickable: true, reason: null }
      : { url, clickable: false, reason: languageReason }
  }

  // Regular slug-driven page: {BASE}/{language}/{...ancestorSlugs}/{slug}
  const ancestors = getAncestors(pages, pageId)
  const ancestorSlugs = ancestors
    .map(a => profileFor(a, language)?.slug || '')
    .filter(Boolean)
  const segments = [language, ...ancestorSlugs]
  if (slug) segments.push(slug)
  const url = `${baseUrl}/${segments.join('/')}`

  if (!published) {
    return { url, clickable: false, reason: 'Unpublished — will 404 until published' }
  }

  const unpublishedAncestor = ancestors.find(a => !a.published)
  if (unpublishedAncestor) {
    return {
      url,
      clickable: false,
      reason: `Ancestor "${displayName(unpublishedAncestor, language)}" is unpublished`,
    }
  }

  if (!languageSupported) {
    return { url, clickable: false, reason: languageReason }
  }

  if (!slug) {
    return { url, clickable: false, reason: `No slug set for ${language.toUpperCase()}` }
  }

  return { url, clickable: true, reason: null }
}
